import ApiClient from '@/services/apiClient';

import i18n from '@/i18n';

const t = (key) => i18n.global.t(key);

const READ_MODE_LABELS = {
  get CsnOnly() { return t('cardProfile.readModes.csnOnly'); },
  get UidOrCsn() { return t('cardProfile.readModes.uidOrCsn'); },
  get UidOnly() { return t('cardProfile.readModes.uidOnly'); },
  get NotUse() { return t('cardProfile.readModes.notUse'); },
};

export class MifareCardProfile {
  read_mode = 'UidOnly';
  app_key = '';
  key_type = 'A';
  block_id = 0;
  offset = 0;
  data_length = 0;
  reverse_order = false;

  static constructFromObject(data, obj) {
    obj = obj || new MifareCardProfile();
    if(data) {
      obj.read_mode = ApiClient.convertToType(data['read_mode'], 'String') ?? 'UidOnly';
      obj.app_key = ApiClient.convertToType(data['app_key'], 'String') ?? '';
      obj.key_type = ApiClient.convertToType(data['key_type'], 'String') ?? 'A';
      obj.block_id = ApiClient.convertToType(data['block_id'], 'Integer') ?? 0;
      obj.offset = ApiClient.convertToType(data['offset'], 'Integer') ?? 0;
      obj.data_length = ApiClient.convertToType(data['data_length'], 'Integer') ?? 0;
      obj.reverse_order = ApiClient.convertToType(data['reverse_order'], 'Boolean') ?? false;
    }
    return obj;
  }

  toJSON() {
    return {
      read_mode: this.read_mode,
      app_key: this.app_key,
      key_type: this.key_type,
      block_id: this.block_id,
      offset: this.offset,
      data_length: this.data_length,
      reverse_order: this.reverse_order,
    };
  }
}

export class DesfireCardProfile {
  read_mode = 'UidOnly';
  app_id = 0;
  app_key = '';
  encryption = 'Clear';
  key_id = 0;
  file_id = 0;
  offset = 0;
  data_length = 0;
  reverse_order = false;

  static constructFromObject(data, obj) {
    obj = obj || new DesfireCardProfile();
    if(data) {
      obj.read_mode = ApiClient.convertToType(data['read_mode'], 'String') ?? 'UidOnly';
      obj.app_id = ApiClient.convertToType(data['app_id'], 'Integer') ?? 0;
      obj.app_key = ApiClient.convertToType(data['app_key'], 'String') ?? '';
      obj.encryption = ApiClient.convertToType(data['encryption'], 'String') ?? 'Clear';
      obj.key_id = ApiClient.convertToType(data['key_id'], 'Integer') ?? 0;
      obj.file_id = ApiClient.convertToType(data['file_id'], 'Integer') ?? 0;
      obj.offset = ApiClient.convertToType(data['offset'], 'Integer') ?? 0;
      obj.data_length = ApiClient.convertToType(data['data_length'], 'Integer') ?? 0;
      obj.reverse_order = ApiClient.convertToType(data['reverse_order'], 'Boolean') ?? false;
    }
    return obj;
  }

  toJSON() {
    return {
      read_mode: this.read_mode,
      app_id: this.app_id,
      app_key: this.app_key,
      encryption: this.encryption,
      key_id: this.key_id,
      file_id: this.file_id,
      offset: this.offset,
      data_length: this.data_length,
      reverse_order: this.reverse_order,
    };
  }
}

export class HaxoCardProfile {
  read_mode = 'UidOnly';

  static constructFromObject(data, obj) {
    obj = obj || new HaxoCardProfile();
    if(data) {
      obj.read_mode = ApiClient.convertToType(data['read_mode'], 'String') ?? 'UidOnly';
    }
    return obj;
  }

  toJSON() {
    return {
      read_mode: this.read_mode,
    };
  }
}

export class StidVCardProfile {
  read_mode = 'UidOnly';
  app_key = '';
  config_name = '';
  offset = 0;
  data_length = 8;
  reverse_order = false;
  site_code = 0;

  static constructFromObject(data, obj) {
    obj = obj || new StidVCardProfile();
    if(data) {
      obj.read_mode = ApiClient.convertToType(data['read_mode'], 'String') ?? 'UidOnly';
      obj.app_key = ApiClient.convertToType(data['app_key'], 'String') ?? '';
      obj.config_name = ApiClient.convertToType(data['config_name'], 'String') ?? '';
      obj.offset = ApiClient.convertToType(data['offset'], 'Integer') ?? 0;
      obj.data_length = ApiClient.convertToType(data['data_length'], 'Integer') ?? 8;
      obj.reverse_order = ApiClient.convertToType(data['reverse_order'], 'Boolean') ?? false;
      obj.site_code = ApiClient.convertToType(data['site_code'], 'Integer') ?? 0;
    }
    return obj;
  }

  toJSON() {
    return {
      read_mode: this.read_mode,
      app_key: this.app_key,
      config_name: this.config_name,
      offset: this.offset,
      data_length: this.data_length,
      reverse_order: this.reverse_order,
      site_code: this.site_code,
    };
  }
}

export default class CardProfile {
  id = 0;
  type = 'MIFARE';
  mifare_card_profile = new MifareCardProfile();
  desfire_card_profile = new DesfireCardProfile();
  haxo_card_profile = new HaxoCardProfile();
  stid_vcard_profile = new StidVCardProfile();

  constructor(type = 'MIFARE', id = 0) {
    this.type = type;
    this.id = id;
  }

  static constructFromObject(data, obj) {
    obj = obj || new CardProfile();
    if(data) {
      obj.id = ApiClient.convertToType(data['id'], 'Integer') ?? 0;
      if(data['desfire_card_data']) {
        obj.type = 'DESFIRE';
        obj.desfire_card_profile = DesfireCardProfile.constructFromObject(data['desfire_card_data']);
      } else if(data['mifare_card_data']) {
        obj.type = 'MIFARE';
        obj.mifare_card_profile = MifareCardProfile.constructFromObject(data['mifare_card_data']);
      } else if(data['stid_vcard']) {
        obj.type = 'STID_VCARD';
        obj.stid_vcard_profile = StidVCardProfile.constructFromObject(data['stid_vcard']);
      } else {
        obj.type = 'HAXO';
        obj.haxo_card_profile = HaxoCardProfile.constructFromObject(data['haxo_secure']);
      }
    }
    return obj;
  }

  clone() {
    return CardProfile.constructFromObject(this.toJSONWithId());
  }

  toJSONWithId() {
    return {
      id: this.id,
      ...this.toJSON(),
    };
  }

  get activeProfile() {
    switch(this.type) {
      case 'DESFIRE':
        return this.desfire_card_profile;
      case 'MIFARE':
        return this.mifare_card_profile;
      case 'STID_VCARD':
        return this.stid_vcard_profile;
      default:
        return this.haxo_card_profile;
    }
  }

  toJSON() {
    switch(this.type) {
      case 'DESFIRE':
        return { desfire_card_data: this.desfire_card_profile };
      case 'MIFARE':
        return { mifare_card_data: this.mifare_card_profile };
      case 'STID_VCARD':
        return { stid_vcard: this.stid_vcard_profile };
      default:
        return { haxo_secure: this.haxo_card_profile };
    }
  }

  get readMode() {
    return this.activeProfile?.read_mode;
  }

  get typeLabel() {
    switch(this.type) {
      case 'HAXO':
        return 'HAXO SECURE';
      case 'STID_VCARD':
        return 'STid Mobile ID';
      default:
        return this.type;
    }
  }

  toString() {
    return `${this.typeLabel} - ${READ_MODE_LABELS[this.readMode] ?? t('cardProfile.disabled')}`;
  }
}
